// SPDX-License-Identifier: MIT
/**
 * WebGL2 post chain. Canvas 2D draws each motion-blur subframe; this pipeline accumulates them in a
 * half-float buffer and applies the look. Runs on SwiftShader (CPU) in the renderer and on the
 * user's GPU in the Player.
 */
import type { FrameFx } from '../compositor.ts';
import { hash01 } from '../core/rng.ts';
import type { PostParams } from '../style.ts';
import { ACCUMULATE_FS, ACCUMULATE_OVERLAY_FS, BLUR_FS, BRIGHT_FS, COMPOSITE_FS, COPY_FS, FULLSCREEN_VS, YUV420_FS, } from './shaders.ts';
type Target = {
    fbo: WebGLFramebuffer;
    tex: WebGLTexture;
    w: number;
    h: number;
};
export type PostOptions = {
    quality?: PostQuality;
    /**
     * Blit each finished frame to the canvas (default true). Hosts that read pixels directly
     * (readRgba / readYuv420) can turn it off to skip a full-frame copy.
     */
    present?: boolean;
};
type Program = {
    prog: WebGLProgram;
    loc: (name: string) => WebGLUniformLocation | null;
};
export type PostQuality = 'full' | 'draft';
export class PostPipeline {
    readonly gl: WebGL2RenderingContext;
    readonly width: number;
    readonly height: number;
    private readonly quality: PostQuality;
    private readonly present: boolean;
    private readonly out: Target;
    private yuv: Target | null = null;
    private readonly sceneTex: WebGLTexture;
    private readonly noiseTex: WebGLTexture;
    private sceneTexReady = false;
    private readonly accum: Target;
    /** Crisp layer, allocated on first use. */
    private overlay: {
        tex: WebGLTexture;
        accum: Target;
    } | null = null;
    /** The crisp layer received a subframe since the last beginFrame(). */
    private overlayUsed = false;
    private readonly half: [
        Target,
        Target
    ];
    private readonly quarter: [
        Target,
        Target
    ];
    private readonly programs: Record<'accumulate' | 'accumulateOverlay' | 'bright' | 'blur' | 'copy' | 'composite' | 'yuv', Program>;
    constructor(gl: WebGL2RenderingContext, width: number, height: number, opts: PostOptions = {}) {
        this.gl = gl;
        this.width = width;
        this.height = height;
        this.quality = opts.quality ?? 'full';
        this.present = opts.present ?? true;
        if (!gl.getExtension('EXT_color_buffer_float'))
            throw new Error('EXT_color_buffer_float unavailable');
        gl.bindVertexArray(gl.createVertexArray());
        this.sceneTex = this.texture(width, height, gl.RGBA8);
        this.noiseTex = this.noiseTexture(256);
        this.accum = this.target(width, height);
        this.out = this.target(width, height, gl.RGBA8);
        const hw = Math.max(1, width >> 1);
        const hh = Math.max(1, height >> 1);
        this.half = [this.target(hw, hh), this.target(hw, hh)];
        this.quarter = [
            this.target(Math.max(1, hw >> 1), Math.max(1, hh >> 1)),
            this.target(Math.max(1, hw >> 1), Math.max(1, hh >> 1)),
        ];
        this.programs = {
            accumulate: this.program(ACCUMULATE_FS),
            accumulateOverlay: this.program(ACCUMULATE_OVERLAY_FS),
            bright: this.program(BRIGHT_FS),
            blur: this.program(BLUR_FS),
            copy: this.program(COPY_FS),
            composite: this.program(COMPOSITE_FS),
            yuv: this.program(YUV420_FS),
        };
    }
    private texture(w: number, h: number, internal: number): WebGLTexture {
        const { gl } = this;
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texStorage2D(gl.TEXTURE_2D, 1, internal, w, h);
        return tex;
    }
    /** Deterministic white-noise tile for film grain (sampled with a per-frame offset). */
    private noiseTexture(size: number): WebGLTexture {
        const { gl } = this;
        const data = new Uint8Array(size * size);
        for (let i = 0; i < data.length; i++)
            data[i] = Math.floor(hash01(i, 0x5eed) * 256);
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, size, size, 0, gl.RED, gl.UNSIGNED_BYTE, data);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        return tex;
    }
    private target(w: number, h: number, internal: number = this.gl.RGBA16F): Target {
        const { gl } = this;
        const tex = this.texture(w, h, internal);
        const fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE)
            throw new Error('incomplete framebuffer');
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return { fbo, tex, w, h };
    }
    private program(fs: string): Program {
        const { gl } = this;
        const compile = (type: number, src: string) => {
            const sh = gl.createShader(type)!;
            gl.shaderSource(sh, src);
            gl.compileShader(sh);
            if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS))
                throw new Error(`shader: ${gl.getShaderInfoLog(sh)}`);
            return sh;
        };
        const prog = gl.createProgram();
        gl.attachShader(prog, compile(gl.VERTEX_SHADER, FULLSCREEN_VS));
        gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fs));
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
            throw new Error(`link: ${gl.getProgramInfoLog(prog)}`);
        const cache = new Map<string, WebGLUniformLocation | null>();
        return {
            prog,
            loc: (name) => {
                if (!cache.has(name))
                    cache.set(name, gl.getUniformLocation(prog, name));
                return cache.get(name)!;
            },
        };
    }
    private pass(p: Program, target: Target | null, textures: WebGLTexture[], uniforms: (p: Program) => void) {
        const { gl } = this;
        gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fbo : null);
        gl.viewport(0, 0, target ? target.w : this.width, target ? target.h : this.height);
        gl.useProgram(p.prog);
        textures.forEach((tex, i) => {
            gl.activeTexture(gl.TEXTURE0 + i);
            gl.bindTexture(gl.TEXTURE_2D, tex);
        });
        uniforms(p);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    beginFrame(): void {
        const { gl } = this;
        gl.viewport(0, 0, this.width, this.height);
        gl.clearColor(0, 0, 0, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.accum.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
        if (this.overlay && this.overlayUsed) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.overlay.accum.fbo);
            gl.clear(gl.COLOR_BUFFER_BIT);
        }
        this.overlayUsed = false;
    }
    /** Upload one Canvas 2D subframe and add it to the accumulator with the given weight. */
    accumulate(source: TexImageSource, weight: number): void {
        const { gl } = this;
        gl.bindTexture(gl.TEXTURE_2D, this.sceneTex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
        this.sceneTexReady = true;
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.pass(this.programs.accumulate, this.accum, [this.sceneTex], (p) => {
            gl.uniform1i(p.loc('uScene'), 0);
            gl.uniform1f(p.loc('uWeight'), weight);
        });
        gl.disable(gl.BLEND);
    }
    /**
     * Add one subframe of the crisp layer (a transparent canvas). Subframes in which no scene drew
     * crisp content are simply not added: they contribute nothing.
     */
    accumulateOverlay(source: TexImageSource, weight: number): void {
        const { gl } = this;
        if (!this.overlay) {
            this.overlay = {
                tex: this.texture(this.width, this.height, gl.RGBA8),
                accum: this.target(this.width, this.height),
            };
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.overlay.accum.fbo);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
        }
        gl.bindTexture(gl.TEXTURE_2D, this.overlay.tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.pass(this.programs.accumulateOverlay, this.overlay.accum, [this.overlay.tex], (p) => {
            gl.uniform1i(p.loc('uScene'), 0);
            gl.uniform1f(p.loc('uWeight'), weight);
        });
        gl.disable(gl.BLEND);
        this.overlayUsed = true;
    }
    /** Bloom + composite to the default framebuffer. */
    finish(post: PostParams, fx: FrameFx, frame: number): void {
        const { gl } = this;
        if (!this.sceneTexReady)
            throw new Error('finish() called before accumulate()');
        const draft = this.quality === 'draft';
        const [h0, h1] = this.half;
        const [q0, q1] = this.quarter;
        this.pass(this.programs.bright, h0, [this.accum.tex], (p) => {
            gl.uniform1i(p.loc('uSrc'), 0);
            gl.uniform1f(p.loc('uThreshold'), post.bloomThreshold);
        });
        const blur = (a: Target, b: Target) => {
            this.pass(this.programs.blur, b, [a.tex], (p) => {
                gl.uniform1i(p.loc('uSrc'), 0);
                gl.uniform2f(p.loc('uDir'), 1 / a.w, 0);
            });
            this.pass(this.programs.blur, a, [b.tex], (p) => {
                gl.uniform1i(p.loc('uSrc'), 0);
                gl.uniform2f(p.loc('uDir'), 0, 1 / a.h);
            });
        };
        blur(h0, h1);
        if (!draft) {
            this.pass(this.programs.copy, q0, [h0.tex], (p) => gl.uniform1i(p.loc('uSrc'), 0));
            blur(q0, q1);
        }
        this.pass(this.programs.composite, this.out, [
            this.accum.tex,
            h0.tex,
            draft ? h0.tex : q0.tex,
            this.noiseTex,
            this.overlay?.accum.tex ?? this.accum.tex,
        ], (p) => {
            gl.uniform1i(p.loc('uAccum'), 0);
            gl.uniform1i(p.loc('uBloomHalf'), 1);
            gl.uniform1i(p.loc('uBloomQuarter'), 2);
            gl.uniform1i(p.loc('uNoise'), 3);
            gl.uniform1i(p.loc('uOverlay'), 4);
            gl.uniform1f(p.loc('uOverlayOn'), this.overlayUsed ? 1 : 0);
            gl.uniform2f(p.loc('uBloom'), post.bloomIntensity[0] * fx.bloom, draft ? 0 : post.bloomIntensity[1] * fx.bloom);
            gl.uniform1f(p.loc('uAberration'), draft ? 0 : post.aberration * fx.lens * (1 + 2.5 * fx.aberration));
            gl.uniform1f(p.loc('uDistortion'), draft ? 0 : post.distortion * fx.lens);
            gl.uniform1f(p.loc('uVignette'), post.vignette * fx.vignette);
            gl.uniform1f(p.loc('uGrain'), draft ? 0 : post.grain * fx.grain);
            gl.uniform1f(p.loc('uExposure'), fx.exposure);
            gl.uniform1f(p.loc('uContrast'), fx.contrast);
            gl.uniform1f(p.loc('uSaturation'), fx.saturation);
            gl.uniform3f(p.loc('uTint'), fx.tint[0], fx.tint[1], fx.tint[2]);
            gl.uniform1f(p.loc('uFlash'), fx.flash);
            // Whole-texel offsets keep NEAREST sampling exact, so grain is identical across runs.
            gl.uniform2f(p.loc('uNoiseOffset'), Math.floor(hash01(frame, 1) * 256) / 256, Math.floor(hash01(frame, 2) * 256) / 256);
            gl.uniform1f(p.loc('uAspect'), this.height / this.width);
        });
        if (this.present) {
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.out.fbo);
            gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
            gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        }
    }
    /** Read the last finished frame as RGBA (rows bottom-up). */
    readRgba(dst: Uint8Array): void {
        const { gl } = this;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.out.fbo);
        gl.readPixels(0, 0, this.width, this.height, gl.RGBA, gl.UNSIGNED_BYTE, dst);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    /** Byte length of one yuv420p frame. */
    get yuv420Bytes(): number {
        return (this.width * this.height * 3) / 2;
    }
    /** Read the last finished frame as planar yuv420p (BT.709 limited range). Requires even sizes, width % 8 = 0. */
    readYuv420(dst: Uint8Array): void {
        const { gl } = this;
        if (this.width % 8 !== 0 || this.height % 2 !== 0)
            throw new Error('yuv420 readback needs width % 8 = 0 and even height');
        this.yuv ??= this.target(this.width / 4, (this.height * 3) / 2, gl.RGBA8);
        this.pass(this.programs.yuv, this.yuv, [this.out.tex], (p) => {
            gl.uniform1i(p.loc('uSrc'), 0);
            gl.uniform2i(p.loc('uSize'), this.width, this.height);
        });
        gl.readPixels(0, 0, this.yuv.w, this.yuv.h, gl.RGBA, gl.UNSIGNED_BYTE, dst);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
}
